import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import watermarkSvg from '../../assets/livinpp-logo-watermark.svg';
import {
    PreparedWatermarkImage,
    WatermarkPosition,
    WatermarkSize,
    prepareWatermarkedImage,
} from '../../utils/watermarkPreloader';

interface SimpleWatermarkProps {
    src: string;
    alt: string;
    className?: string;
    watermarkOpacity?: number;
    watermarkPosition?: WatermarkPosition;
    watermarkSize?: WatermarkSize;
    preparedImage?: PreparedWatermarkImage | null;
}

export const SimpleWatermark: React.FC<SimpleWatermarkProps> = ({
    src,
    alt,
    className,
    watermarkOpacity = 0.5,
    watermarkPosition = 'center',
    watermarkSize = 'large',
    preparedImage = null,
}) => {
    const [resolvedImage, setResolvedImage] = useState<PreparedWatermarkImage | null>(preparedImage);
    const [showFallback, setShowFallback] = useState(Boolean(preparedImage?.needsOverlay));

    useEffect(() => {
        let isCancelled = false;

        if (preparedImage) {
            setResolvedImage(preparedImage);
            setShowFallback(preparedImage.needsOverlay);
            return () => {
                isCancelled = true;
            };
        }

        setResolvedImage(null);
        setShowFallback(false);

        if (!src) {
            return () => {
                isCancelled = true;
            };
        }

        prepareWatermarkedImage({
            src,
            watermarkOpacity,
            watermarkPosition,
            watermarkSize,
        }).then((result) => {
            if (isCancelled) {
                return;
            }

            setResolvedImage(result);
            setShowFallback(result.needsOverlay);
        }).catch(() => {
            if (isCancelled) {
                return;
            }

            setResolvedImage({
                sourceSrc: src,
                displaySrc: src,
                needsOverlay: true,
            });
            setShowFallback(true);
        });

        return () => {
            isCancelled = true;
        };
    }, [preparedImage, src, watermarkOpacity, watermarkPosition, watermarkSize]);

    const displaySrc = resolvedImage?.displaySrc || src;

    return (
        <Container className={className}>
            {displaySrc && <DisplayedImage src={displaySrc} alt={alt} />}
            {showFallback && (
                <FallbackOverlay $position={watermarkPosition}>
                    <FallbackWatermark
                        src={watermarkSvg}
                        alt="Watermark"
                        $opacity={watermarkOpacity}
                        $size={watermarkSize}
                    />
                </FallbackOverlay>
            )}
        </Container>
    );
};

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const DisplayedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

const FallbackOverlay = styled.div<{ $position: string }>`
  position: absolute;
  z-index: 5;
  pointer-events: none;
  ${({ $position }) => {
        switch ($position) {
            case 'top-left':
                return 'top: 10%; left: 10%;';
            case 'top-right':
                return 'top: 10%; right: 10%;';
            case 'bottom-left':
                return 'bottom: 10%; left: 10%;';
            case 'bottom-right':
                return 'bottom: 10%; right: 10%;';
            default:
                return 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
        }
    }}
`;

const FallbackWatermark = styled.img<{ $opacity: number; $size: string }>`
  opacity: ${({ $opacity }) => $opacity};
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.8));
  width: ${({ $size }) => {
        // Match canvas ratios with CSS fallback percentages
        switch ($size) {
            case 'small': return '40%';
            case 'medium': return '50%';
            case 'large': return '60%';
            case 'xlarge': return '65%';
            default: return '60%';
        }
    }};
  height: auto;
  object-fit: contain;
  max-width: none;
  max-height: none;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
`;
